// Figma's icon set IS Phosphor - match it here instead of lucide lookalikes so
// the campaign nav icons line up with the main nav (House/Gear/etc.) and Figma.
import {
  Notebook,
  ChartBar,
  Users,
  UserCheck,
  FileText,
  FilmSlate,
  ChartLine,
  Gear,
  ShoppingBag,
  MagnifyingGlass,
  UserFocus,
  Basket,
  Tray,
  AddressBook,
  EnvelopeSimple,
  Funnel,
  UsersThree,
} from '../../design-system/icons';
import type { NavItem } from '../../components/common/AppSidebar';
import type { CampaignPermissions } from '../../lib/campaignPermissions';
import type { CampaignPipeline } from '@discovr/types';
import { DISCOVERY_TAB, type DiscoverySubView } from '../search';
import { resolvePipeline } from './campaignPipeline';

const DISCOVERY_GROUP = 'Discovery';

const discoveryItem = (sub: DiscoverySubView, item: Omit<NavItem, 'id' | 'sub'>): NavItem => ({
  id: DISCOVERY_TAB,
  sub,
  ...item,
});

const DISCOVERY_ITEMS: readonly NavItem[] = [
  discoveryItem('find', {
    label: 'Find creators',
    icon: MagnifyingGlass,
    groupLabel: DISCOVERY_GROUP,
    groupIcon: UsersThree,
  }),
  discoveryItem('personas', { label: 'Persona', icon: UserFocus }),
  discoveryItem('pool', { label: 'Pool', icon: Basket }),
];

export interface NavBuilderOptions {
  permissions: CampaignPermissions;
  counts: {
    outreach?: number;
    creators?: number;
    confirmed?: number;
    scripts?: number;
    content?: number;
    liveContent?: number;
  };
  /** Show the Shopify nav item only when the campaign has Shopify enabled. */
  shopifyEnabled?: boolean;
  /** Show the Outreach (creator sourcing) nav item. Admin-only — brand and the
   *  public campaign shell never pass this, so it stays out of their nav. */
  showOutreach?: boolean;
  /** Canonical campaign platform. X drops the Scripts lane. */
  platform?: string | null;
  /** Production stages the campaign runs; absent = all. Disabled lanes drop their tab. */
  pipeline?: CampaignPipeline | null;
  showDiscovery?: boolean;
}

/**
 * Build the campaign detail sidebar navigation items from permissions and counts.
 * Both Brand and Admin wrappers call this instead of constructing nav inline.
 * Reporting and Live Content are visible to all roles.
 */
export function buildNavItems(options: NavBuilderOptions): NavItem[] {
  const { counts } = options;
  const pipeline = resolvePipeline(options.pipeline);

  const items: NavItem[] = [...(options.showDiscovery ? DISCOVERY_ITEMS : [])];

  const reporting: NavItem = { id: 'reporting', label: 'Reporting', icon: ChartBar };
  if (options.showOutreach) {
    items.push(
      {
        id: 'outreach',
        sub: 'inbox',
        label: 'Inbox',
        icon: Tray,
        count: counts.outreach,
        groupLabel: 'Outreach',
        groupIcon: EnvelopeSimple,
      },
      { id: 'outreach', sub: 'templates', label: 'Templates', icon: FileText },
      { id: 'outreach', sub: 'crm', label: 'CRM', icon: AddressBook },
    );
  } else {
    items.push(options.showDiscovery ? { ...reporting, dividerAbove: true } : reporting);
  }

  const pipelineGroup: Pick<NavItem, 'groupLabel' | 'groupIcon'> = options.showOutreach
    ? { groupLabel: 'Pipeline', groupIcon: Funnel }
    : {};
  items.push(
    {
      id: 'creators',
      label: 'Creators',
      icon: Users,
      count: counts.creators,
      ...pipelineGroup,
    },
    { id: 'confirmed', label: 'Confirmed', icon: UserCheck, count: counts.confirmed },
  );

  // A lane turned off stays visible while creators are still in it.
  if (pipeline.script_enabled || (counts.scripts ?? 0) > 0) {
    items.push({ id: 'scripts', label: 'Scripts', icon: FileText, count: counts.scripts });
  }

  if (pipeline.content_enabled || (counts.content ?? 0) > 0) {
    items.push({
      id: 'content',
      label: 'Content',
      icon: FilmSlate,
      count: counts.content,
    });
  }

  items.push({ id: 'live-content', label: 'Live', icon: ChartLine, count: counts.liveContent });

  if (options.showOutreach) items.push({ ...reporting, dividerAbove: true });

  // Shopify coupons - only shown when the campaign has Shopify enabled.
  if (options.shopifyEnabled) {
    items.push({ id: 'shopify', label: 'Shopify', icon: ShoppingBag });
  }

  // Details + Settings (+ Shopify, when shown) form the bottom cluster
  // (matches Figma). CampaignNavSidebar renders its own structural divider
  // above the whole footer-ID group — no per-item dividerAbove needed here,
  // that would double up with it. Details moved down from the top to sit
  // directly above Settings.
  items.push({ id: 'details', label: 'Details', icon: Notebook });

  // The campaign's only Settings entry (outreach settings live in its Outreach
  // sub-tab) - always visible; sub-tab visibility is gated inside
  // CampaignSettingsTab itself.
  items.push({ id: 'settings', label: 'Settings', icon: Gear });

  return items;
}
